import { decode } from 'html-entities';
import findIndex from 'lodash/findIndex';
import isString from 'lodash/isString';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Button from '../components/Button';
import Carousel from '../components/Carousel';
import IconStar from '../components/Icons/Star';
import JsonSelect from '../components/JsonSelect/JsonSelect';
import List from '../components/List';
import ListItem from '../components/ListItem';
import ListItemButton from '../components/ListItemButton';
import ListItemIcon from '../components/ListItemIcon';
import ListSubheader from '../components/ListSubheader';
import Message from '../components/Message';
import MessageDate from '../components/MessageDate';
import MessageWolframAlpha from '../components/MessageWolframAlpha';
import RelatedQuestion from '../components/RelatedQuestion';
import RelatedQuestionsList from '../components/RelatedQuestionsList';
import TagBlock from '../components/TagBlock';
import {
  Attachment,
  CarouselMessage,
  EscalationMessages,
  HTMLMessage,
  HTMLMessageWithScroll,
  InfoMessage,
  JsonSelectMessage,
  QuestionsMessage,
  QuestionsMessageProjects,
  RelatedQuestionsButton,
  TextMessage,
  TextMessageWithTranslation,
  TreeMessage
} from '../models';
import { useStores } from '../stores';
import {
  detectBrowser,
  formatLocale,
  getFontSize,
  getMessageOrder as getOrder,
  MESSAGE_TREE_TYPE_BRANCH,
  MESSAGE_TREE_TYPE_COMMENT,
  MESSAGE_TREE_TYPE_LINK_TO_URL,
  MESSAGE_TYPE_RECEIVED,
  MESSAGE_TYPE_SENT,
  sanitizeHTML
} from '../utilities';

/**
 * At the moment, the function may not display some elements.
 *
 * Nedd info about:
 * - video
 * - image
 * - voice/sound message
 * - height of the Message is one-two lines to set option "actionsHorizontalPosition"
 */
export default function useMessageRenderer() {
  const { t, i18n } = useTranslation();
  const { bot, config } = useStores();

  const textFontSize = getFontSize(config.message);

  return React.useCallback(
    (message, index, messages, ref, messageRef) => {
      const prevMessage = messages[index - 1] || {};
      const nextMessage = messages[index + 1] || {};

      const isUserMessage = findIndex(
        messages.slice(),
        messageItem => messageItem.type === MESSAGE_TYPE_SENT
      );
      const indexFirstUserMessage = isUserMessage >= 0 ? isUserMessage : messages.length;
      const showDate = index >= indexFirstUserMessage;

      /**
       * @bug create more suitable check
       */
      const isTreeActionsMessage =
        (message instanceof TreeMessage &&
          Array.isArray(message.content) &&
          message.content.filter(x => x.Type === MESSAGE_TREE_TYPE_BRANCH).length !== 0) ||
        (message instanceof TreeMessage && message.content.Type === MESSAGE_TREE_TYPE_BRANCH);

      const isTreeURLMessage =
        (message instanceof TreeMessage &&
          Array.isArray(message.content) &&
          message.content.filter(x => x.Type === MESSAGE_TREE_TYPE_LINK_TO_URL).length !== 0) ||
        (message instanceof TreeMessage && message.content.Type === MESSAGE_TREE_TYPE_LINK_TO_URL);

      const isInnerTreeCommentMessage =
        (message instanceof TreeMessage &&
          messages[index - 1].type === MESSAGE_TYPE_RECEIVED &&
          Array.isArray(message.content) &&
          message.content.filter(x => x.Type === MESSAGE_TREE_TYPE_COMMENT).length !== 0) ||
        (message instanceof TreeMessage &&
          message.content.Type === MESSAGE_TREE_TYPE_COMMENT &&
          messages[index - 1].type === MESSAGE_TYPE_RECEIVED);

      const isRelatedQuestionsButton =
        message instanceof RelatedQuestionsButton &&
        Array.isArray(message.content) &&
        message.content.length !== 0;

      const isCarouselMessage = message instanceof CarouselMessage;

      const getBackgroundColor = () => {
        if (
          isTreeActionsMessage ||
          isTreeURLMessage ||
          isRelatedQuestionsButton ||
          isCarouselMessage
        ) {
          return 'transparent';
        }

        if (message.type === MESSAGE_TYPE_RECEIVED) {
          return config.message.botBackgroundColor;
        }

        return config.message.userBackgroundColor;
      };

      const getTextColor = () => {
        if (message.type === MESSAGE_TYPE_RECEIVED) {
          return config.message.botColor;
        }

        return config.message.userColor;
      };

      const renderHTMLContent = (content, internalNotes) => {
        if (!internalNotes) {
          return (
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(content)
              }}
            />
          );
        }

        return (
          <div>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(content)
              }}
            />
            <Title>{t('internalNotes')}</Title>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(internalNotes)
              }}
            />
          </div>
        );
      };

      const renderBranchNode = (
        // eslint-disable-next-line react/prop-types
        {
          ProjectID: ProjectId,
          MesID: MesId,
          MainGPFID: GPFId,
          GPFToId: ParentNodeId,
          Comment,
          CrossProjectNameTree: CrossProjectName,
          language
        },
        idx
      ) => {
        const commentStr = String(Comment);
        return (
          <ButtonTree
            key={idx}
            type="button"
            textFontFamily={config.fontFamily}
            textFontSize={textFontSize}
            color={config.button.color}
            hoverColor={config.button.hoverColor}
            borderRadius={config.button.borderRadius}
            onClick={() => {
              bot.runTreeSequence({
                ProjectId,
                MesId,
                GPFId,
                ParentNodeId,
                SourceLanguage: language,
                Comment,
                CrossProjectName
              });
            }}
          >
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(decode(commentStr))
              }}
            />
          </ButtonTree>
        );
      };

      const renderURLNode = (
        // eslint-disable-next-line react/prop-types
        { Comment, Data },
        idx
      ) => {
        return (
          <ButtonTree
            key={idx}
            type="button"
            textFontFamily={config.fontFamily}
            textFontSize={textFontSize}
            color={config.button.color}
            hoverColor={config.button.hoverColor}
            borderRadius={config.button.borderRadius}
            onClick={() => {
              window.open(Data);
            }}
          >
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(decode(Comment))
              }}
            />
          </ButtonTree>
        );
      };

      const renderTreeContent = (content, idx, language) => {
        switch (content.Type) {
          case MESSAGE_TREE_TYPE_BRANCH:
            return renderBranchNode(content, idx, language);
          case MESSAGE_TREE_TYPE_LINK_TO_URL:
            return renderURLNode(content, idx);
          case MESSAGE_TREE_TYPE_COMMENT:
            return (
              <div key={idx}>
                {content.CrossProjectNameTree && (
                  <TagBlock>{content.CrossProjectNameTree}</TagBlock>
                )}
                <p
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(decode(content.Comment))
                  }}
                />
              </div>
            );
          default:
            return null;
        }
      };

      const renderInfoContent = (title, content) => (
        <List
          subheader={
            <ListSubheader color={config.message.botColor} textFontSize={textFontSize}>
              {t(title)}
            </ListSubheader>
          }
        >
          {content.map(({ MessageText, MesID: MesId }, idx) => (
            <ListItem key={idx}>
              <ListItemButton
                type="button"
                onClick={() => {
                  bot.runInfoSequence({
                    MesId,
                    MessageText,
                    SourceLanguage: formatLocale(i18n.language)
                  });
                }}
                textFontSize={textFontSize}
              >
                {!config.message.disableStarIcon && (
                  <ListItemIcon>
                    <IconStar viewBox="0 0 576 512" fill={config.message.botColor} />
                  </ListItemIcon>
                )}
                {decode(MessageText)}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );

      /**
       * Render `Related Questions` button
       */
      const renderRelatedQuestionsButton = content => {
        return (
          <RelatedQuestion
            classicView
            relatedQuestions={content}
            onRelatedQuestionsShow={questions =>
              bot.showRelatedQuestions('relatedQuestions', questions)
            }
            horizontalPosition={false}
          />
        );
      };

      /**
       * If the user clicks on `Related Questions`,
       * this component will be rendered.
       */
      const renderRelatedQuestions = (title, content) => (
        <RelatedQuestionsList
          subheader={<ListSubheader textFontSize={textFontSize}>{title}</ListSubheader>}
          content={content}
        />
      );

      const renderRelatedQuestionsProjects = (title, content, MainProjectMesId) => (
        <List>
          <MassageP>{title}</MassageP>
          {content.map((question, idx) => (
            <ListItem key={idx}>
              <ButtonBlock
                type="button"
                onClick={() => {
                  bot.askQuestion({
                    ProjectId: question.ProjectId,
                    ProjectName: question.ProjectName,
                    Question: question.Message,
                    TextLanguage: i18n.language,
                    MainProjectMesId
                  });
                  localStorage.setItem('rightQuestionClicked', true);
                }}
              >
                {question.ProjectName && <TagBlock>{question.ProjectName}</TagBlock>}
                {decode(question.Message)}
              </ButtonBlock>
            </ListItem>
          ))}
        </List>
      );

      /**
       * Show Escalation Message, if
       * noAnswersCount >= config.escalationMessages.noAnswersCount
       */
      const renderEscalationMessages = () => (
        <div
          dangerouslySetInnerHTML={{
            __html: sanitizeHTML(t('escalationMessage'))
          }}
        />
      );

      /**
       * Show Message with carousel
       */
      const renderCarouselMessage = (
        { hotTopics, popularQuestions, content },
        jobSearch,
        sourceLanguage
      ) => {
        return (
          <Carousel
            hotTopics={hotTopics}
            popularQuestions={popularQuestions}
            content={content}
            jobSearch={jobSearch}
            sourceLanguage={sourceLanguage}
          />
        );
      };

      /**
       * Show Message for WolframAlpha response
       */
      const renderWolframAlphaMessage = ({ text, image, question }) => {
        return <MessageWolframAlpha text={text} question={question} image={image} />;
      };

      /**
       * Show Message for custom select using special json-file
       */
      const renderJsonSelectlMessage = ({ data, text }) => {
        return <JsonSelect intentData={data} text={text} />;
      };

      const renderContent = () => {
        switch (message.constructor) {
          case TextMessage:
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(message.content)
                }}
              />
            );
          case TextMessageWithTranslation:
            return (
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(t(message.content))
                }}
              />
            );
          case HTMLMessage:
            if (message.original.isWolframAlphaMessage) {
              return renderWolframAlphaMessage(message.content);
            }
            return renderHTMLContent(message.content, message.original.internalNotes || '');
          case HTMLMessageWithScroll:
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(message.content)
                }}
              />
            );
          case TreeMessage:
            return Array.isArray(message.content)
              ? message.content
                  .map(item => {
                    item.CrossProjectNameTree = message.original.crossProjectNameTree;
                    item.language = message.original.language;
                    return item;
                  })
                  .map(renderTreeContent)
              : renderTreeContent(
                  {
                    ...message.content,
                    CrossProjectNameTree: message.original.crossProjectNameTree,
                    language: message.original.language
                  },
                  new Date()
                );
          case InfoMessage:
            return renderInfoContent(message.title, message.content);
          case QuestionsMessage:
            return renderRelatedQuestions(t(message.title), message.content);
          case QuestionsMessageProjects:
            return renderRelatedQuestionsProjects(
              t(message.title),
              message.content,
              message.original.MainProjectMesId
            );
          case EscalationMessages:
            return renderEscalationMessages();
          case RelatedQuestionsButton:
            return renderRelatedQuestionsButton(message.content);
          case CarouselMessage:
            return renderCarouselMessage(
              message.content,
              message.original.jobSearch,
              message.original.sourceLanguage
            );
          case JsonSelectMessage:
            return renderJsonSelectlMessage(message.content);
          default:
            return null;
        }
      };

      // for IE
      const isUserMessageType = message.type === MESSAGE_TYPE_SENT;
      const isInfoMessage = message instanceof InfoMessage;
      const isAttachmentMessage = message instanceof Attachment;
      const isQuestionsMessage = message instanceof QuestionsMessage;
      const isHTMLMessageWithScroll = message instanceof HTMLMessageWithScroll;
      const isJsonSelectMessage = message instanceof JsonSelectMessage;
      const shouldScrollIntoView =
        isUserMessageType ||
        isInfoMessage ||
        isAttachmentMessage ||
        isQuestionsMessage ||
        isHTMLMessageWithScroll ||
        isJsonSelectMessage;

      if (typeof message.content === 'string') {
        const isUserInput = message.content.includes('{input_name}');
        if (isUserInput && bot.formFields.input_name) {
          message.content = message.content.replaceAll(
            '{input_name}',
            `<b>${bot.formFields.input_name}</b>`
          );
        }
      }

      return (
        <div
          key={index}
          ref={detectBrowser.isIE && shouldScrollIntoView ? messageRef : node => ref(node, message)}
        >
          {!isTreeActionsMessage &&
            !isTreeURLMessage &&
            !isInnerTreeCommentMessage &&
            !isRelatedQuestionsButton &&
            isString(config.message.dateFormat) &&
            showDate &&
            message.title !== 'suggestedQuestions' &&
            !message.original.isShowMessageDate && (
              <MessageDate
                messageType={message.type}
                label={
                  message.type === MESSAGE_TYPE_RECEIVED
                    ? config.message.label.va
                    : t(config.message.label.user)
                }
              />
            )}
          <Message
            type={message.type}
            orderType={getOrder(prevMessage.type, message.type, nextMessage.type)}
            textFontFamily={config.fontFamily}
            textFontSize={textFontSize}
            textColor={getTextColor(message.type)}
            backgroundColor={getBackgroundColor()}
            buttonColor={config.button.hoverColor}
            buttonBackgroundColor={config.button.color}
            borderRadius={config.message.borderRadius}
            disablePadding={
              isTreeActionsMessage ||
              isTreeURLMessage ||
              isRelatedQuestionsButton ||
              isCarouselMessage
            }
            attachments={message.attachments}
            feedback={message.hasFeedback}
            onLike={() => bot.like(message.original)}
            onDislike={() => bot.dislike(message.original)}
            isDivider={isRelatedQuestionsButton}
            isCarousel={isCarouselMessage}
            MesIndex={index}
            crossProjectName={message.original.crossProjectName}
            answerWithDateTime={message.original.isDateTime}
          >
            {renderContent()}
          </Message>
        </div>
      );
    },
    [
      bot,
      config.button.borderRadius,
      config.button.color,
      config.button.hoverColor,
      config.fontFamily,
      config.message.borderRadius,
      config.message.botBackgroundColor,
      config.message.botColor,
      config.message.dateFormat,
      config.message.disableStarIcon,
      config.message.label.user,
      config.message.label.va,
      config.message.userBackgroundColor,
      config.message.userColor,
      i18n.language,
      t,
      textFontSize
    ]
  );
}

const ButtonTree = styled(Button)`
  font-size: ${props => props.textFontSize}px;
  text-align: left;
`;

const ButtonBlock = styled.div`
  display: flex;
  align-items: baseline;
  line-height: 1.6;
  cursor: pointer;
  transition: color 0.15s;
  &:hover {
    color: #3d56ef;
  }
`;

const MassageP = styled.p`
  color: #666;
  border-bottom: 1px solid #c4d1d8;
  padding: 6px 0;
  margin: 0;
  margin-bottom: 5px;
`;

const Title = styled.p`
  font-weight: bold;
`;
